import { useEffect, useRef } from 'react';
import { UserControlsSchema, type UserControls } from '@shared/me/wire';
import { useUserControlsContext } from '../contexts/UserControlsContext';
import { useApi } from '../lib/api';
import { fetchJson } from '../lib/apiFetchJson';
import { logger } from '../lib/logger';
import { subscribeAppHomeEvents } from '../lib/sseStore';

const STREAM_KEY = { view: 'active', filter: 'all' } as const;

export function identityFingerprint(controls: Pick<UserControls, 'userId' | 'impersonation'>): string {
  const { impersonation, userId } = controls;
  return `${impersonation?.target ?? 'self'}:${impersonation?.actingAsSlackId ?? userId}`;
}

/**
 * Reloads the tab when the server-side identity moved under it. Another tab can
 * start or stop a demo while this one is open, closed, or frozen in the
 * back-forward cache. On every stream open and every bfcache restore, the
 * refetched /api/me (an exit route, reachable in either identity) is compared
 * with the identity this tab booted with; a context-driven change is compared
 * directly. A reload clears cached data that a refusing 404 refetch would
 * leave on screen. A pending check outlives the effect on purpose: the
 * decision to reload must not depend on a re-render.
 */
export function useIdentityGuard(): void {
  const { apiFetch } = useApi();
  const controls = useUserControlsContext();
  const current = controls.userId == null ? null : identityFingerprint(controls);
  const bootRef = useRef<string | null>(null);

  useEffect(() => {
    if (current === null) return;
    bootRef.current ??= current;
    if (current !== bootRef.current) {
      window.location.reload();
      return;
    }
    const check = () => {
      void fetchJson(apiFetch, UserControlsSchema, '/api/me')
        .then((fresh) => {
          if (identityFingerprint(fresh) !== bootRef.current) window.location.reload();
        })
        .catch((err: unknown) => {
          logger.warn({ err }, '[identity-guard] /api/me refetch failed');
        });
    };
    const unsubscribe = subscribeAppHomeEvents(STREAM_KEY, (event) => {
      if (event.type === 'open') check();
    });
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) check();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      unsubscribe();
    };
  }, [apiFetch, current]);
}
